import { partIntervalListQuerySchema, type PartReplacementIntervalView } from '@chukua/common';
import { Select, Switch, Table } from 'antd';
import { Gauge, SearchX, ShieldAlert } from 'lucide-react';
import { useCallback } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useDirectoryList } from '../../../hooks/use-directory-list';
import { buildIntervalColumns } from '../components/build-interval-columns';
import { usePartTypeOptions } from '../hooks/use-part-type-options';
import { listTruckPartIntervals } from '../parts.api';

/**
 * The Part life tab of a truck's own page: how long, and how many km, each part lasted before the next was fitted, with the ones
 * replaced sooner than the part type's expected life marked Short. Unknown distance (no odometer on one of the orders) is not judged by km.
 */
export function TruckPartLifeTab({ truckId }: { truckId: string }) {
  const fetchPage = useCallback((query: Parameters<typeof listTruckPartIntervals>[1]) => listTruckPartIntervals(truckId, query), [truckId]);
  const { state, list, isRefreshing } = useDirectoryList(partIntervalListQuerySchema, fetchPage);
  const partTypes = usePartTypeOptions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <TableSkeleton label="Loading part life" />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load part life" description="Something went wrong loading it. Try reloading the page." />;
  }
  if (state.page.total === 0 && !list.isFiltered && list.query.isShort === undefined) {
    return (
      <EmptyState
        icon={Gauge}
        title="Nothing replaced yet"
        description="Once a part type has been fitted to this truck twice, how long the first one lasted shows here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          mode="multiple"
          allowClear
          loading={partTypes === null}
          className="w-full md:w-64"
          placeholder="Select or search part types"
          aria-label="Part types"
          optionFilterProp="label"
          options={(partTypes ?? []).map((type) => ({ value: type.id, label: type.name }))}
          value={list.query.partTypeId ?? []}
          onChange={(partTypeId: string[]) => list.setQuery({ partTypeId })}
        />
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={list.query.isShort === true} onChange={(on) => list.setQuery({ isShort: on ? true : undefined })} aria-label="Short-lived only" />
          Short-lived only
        </label>
      </div>
      <Table
        rowKey={(row) => row.replacedByPartsLineId}
        dataSource={state.page.items}
        columns={buildIntervalColumns({ list })}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <EmptyState icon={SearchX} title="No matching replacements" description="Nothing matches these filters." /> }}
        {...list.tableProps<PartReplacementIntervalView>(state.page.total)}
      />
    </div>
  );
}
